import { useState } from 'react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, MessageErreur } from '../ui/Primitives';
import type { OptionFiche, OrientationEntretien } from '../../lib/useEntretiens';

const CLASSE_SAISIE =
  'w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-codialis-navy focus:border-codialis-sky focus:outline-none focus:ring-2 focus:ring-codialis-sky/20';

/**
 * Orientation d'un candidat non retenu, demandée au moment de la décision.
 *
 * Quand le profil ne correspond pas à la formation, le candidat n'est pas
 * renvoyé sans suite : on l'adresse à une ou plusieurs structures. Aucune n'est
 * obligatoire — la décision reste possible sans orientation —, mais « Autre »
 * se précise, faute de quoi il ne dirait rien. L'API applique la même règle.
 *
 * Elle s'ouvre par-dessus le compte-rendu, qu'elle ne remplace pas : annuler
 * ramène à la décision, sans rien avoir enregistré.
 */
export function ModaleOrientation({
  interlocuteur,
  options,
  initiale,
  enCours,
  erreur,
  onConfirmer,
  onFermer,
}: {
  interlocuteur: string;
  options: OptionFiche[];
  /** Orientation déjà saisie, reprise quand la décision est corrigée. */
  initiale: OrientationEntretien | null;
  enCours: boolean;
  erreur: string | null;
  onConfirmer: (orientation: OrientationEntretien) => void;
  onFermer: () => void;
}): JSX.Element {
  const [structures, setStructures] = useState<string[]>(initiale?.structures ?? []);
  const [autre, setAutre] = useState(initiale?.autre ?? '');

  const autreCoche = structures.includes('AUTRE');
  const autreManquant = autreCoche && autre.trim() === '';

  const basculer = (code: string): void =>
    setStructures((actuelles) =>
      actuelles.includes(code)
        ? actuelles.filter((valeur) => valeur !== code)
        : [...actuelles, code],
    );

  const confirmer = (): void => {
    if (autreManquant) return;
    onConfirmer({ structures, autre: autreCoche ? autre.trim() : '' });
  };

  return (
    <Modale
      titre="Candidat non retenu"
      sousTitre={interlocuteur}
      ouverte
      onFermer={onFermer}
      largeur="max-w-lg"
      zIndice="z-[60]"
    >
      <div className="space-y-4 px-4 py-5 sm:px-6">
        <fieldset className="space-y-3">
          <legend className="text-sm text-codialis-navy">
            Le profil ne correspond pas à la formation. Vers quelle(s) structure(s) orienter le
            candidat ?
          </legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {options.map((option) => (
              <label
                key={option.code}
                className="flex min-h-[44px] cursor-pointer items-center gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-codialis-navy transition hover:border-codialis-sky/40"
              >
                <input
                  type="checkbox"
                  checked={structures.includes(option.code)}
                  onChange={() => basculer(option.code)}
                  className="h-4 w-4 shrink-0 accent-codialis-mint"
                />
                <span>{option.libelle}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {autreCoche && (
          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-gray">
              Préciser l’orientation « Autre »
            </span>
            <textarea
              rows={2}
              value={autre}
              maxLength={500}
              onChange={(evenement) => setAutre(evenement.target.value)}
              aria-invalid={autreManquant}
              className={CLASSE_SAISIE}
            />
            {autreManquant && (
              <span className="mt-1 block text-xs text-codialis-gray">
                Précision obligatoire pour « Autre ».
              </span>
            )}
          </label>
        )}

        {structures.length === 0 && (
          <p className="text-xs text-codialis-gray">
            Aucune structure cochée : le candidat sera marqué non retenu sans orientation.
          </p>
        )}

        {erreur && <MessageErreur message={erreur} />}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 rounded-b-2xl border-t border-gray-100 bg-white px-4 py-4 sm:px-6">
        <BoutonSecondaire onClick={onFermer} disabled={enCours}>
          Annuler
        </BoutonSecondaire>
        <BoutonPrincipal onClick={confirmer} disabled={enCours || autreManquant}>
          {enCours ? 'Enregistrement…' : 'Confirmer « non retenu »'}
        </BoutonPrincipal>
      </div>
    </Modale>
  );
}
